import {
  ResourceUsageLog_Create,
  ResourceUsageLog_Read,
  ResourceUsageLog_Update_Destroyable,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { BasicEquipment } from '~hooks/useEquipment/models';
import { ResourceUsageLogDTO } from '~pages/Approvals/DriverPay/helpers';
import { Nullable } from '~types/Nullable';

import { DriverBasic } from '../useDrivers/models';
import { Phase } from '../useProjects/models';

export class ResourceUsageLog {
  constructor(
    private _id: Nullable<string>,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _usage_minutes: number,
    private _equipment: Nullable<BasicEquipment>,
    private _driver: DriverBasic,
    private _phase: Nullable<Phase>,
    private _resource_usage_loggable_type: string,
    private _resource_usage_loggable_id: string,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get usageMinutes() {
    return this._usage_minutes;
  }

  public get equipment() {
    return this._equipment;
  }

  public get driver() {
    return this._driver;
  }

  public get phase() {
    return this._phase;
  }

  public get resourceUsageLoggableType() {
    return this._resource_usage_loggable_type;
  }

  public get resourceUsageLoggableId() {
    return this._resource_usage_loggable_id;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: ResourceUsageLog_Read) {
    return new ResourceUsageLog(
      proto.id,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      proto.usage_minutes,
      proto.equipment ? BasicEquipment.parse(proto.equipment) : null,
      DriverBasic.parse(proto.driver),
      proto.phase ? Phase.parse(proto.phase) : null,
      proto.resource_usage_loggable_type,
      proto.resource_usage_loggable_id,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public static deparse(proto: ResourceUsageLogDTO, dateOfUse: string) {
    let data: ResourceUsageLog_Create | ResourceUsageLog_Update_Destroyable;
    if (proto._destroy === 1 && proto.id) {
      data = {
        id: proto.id,
        _destroy: 1,
      };
    } else if (proto.id && !proto._destroy) {
      data = {
        id: proto.id,
        // there are cases where the result of this math is not an integer so we need to round it
        // e.g. 8.2 * 60 = 491.99999999999994
        usage_minutes: Math.round(proto.quantity * 60),
        phase_id: proto.phase?.id ?? null,
        equipment_id: proto.equipment ? proto.equipment.id : undefined,
      };
    } else {
      data = {
        // there are cases where the result of this math is not an integer so we need to round it
        // e.g. 8.2 * 60 = 491.99999999999994
        usage_minutes: Math.round(proto.quantity * 60),
        phase_id: proto.phase?.id ?? null,
        equipment_id: proto.equipment ? proto.equipment.id : undefined,
        driver_id: proto.driver.id ?? '',
        date_of_use: dayjs.tz(dateOfUse).format('YYYY-MM-DD'),
      };
    }
    return data;
  }
}
